import React, { useState } from 'react';
import {
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Collapse,
  IconButton,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Papa from 'papaparse';

const evaluationCriteriaOptions = ['Generation'];

const UploadCSV = ({
  handleCSVUpload,
  isSubmitting,
  modelOptions,
  defaultEmail,
  mode,
}) => {
  const [csvFile, setCsvFile] = useState(null);
  const [questionColumn, setQuestionColumn] = useState('');
  const [modelColumn, setModelColumn] = useState('');
  const [baselineAnswerSourcesColumn, setBaselineAnswerSourcesColumn] = useState('');
  const [baselineChunkColumn, setBaselineChunkColumn] = useState('');
  const [baselineAnswerColumn, setBaselineAnswerColumn] = useState('');
  const [columns, setColumns] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [userEmail, setUserEmail] = useState(defaultEmail || '');
  const [evaluationCriteria, setEvaluationCriteria] = useState([]);
  const [showHelp, setShowHelp] = useState(false);

  const expectedColumns = {
    questionColumn: ['question', 'query', 'prompt'],
    modelColumn: ['model', 'tenant_model', 'ai_model'],
    baselineAnswerSourcesColumn: ['answer_sources', 'source', 'baseline_sources'],
    baselineChunkColumn: ['chunks', 'data_chunks', 'text_chunks'],
    baselineAnswerColumn: ['answer', 'baseline_answer', 'response'],
  };

  const findBestMatch = (csvCols, options) => {
    const lows = options.map((o) => o.toLowerCase());
    for (const col of csvCols) {
      const lc = col.toLowerCase();
      if (lows.includes(lc)) return col;
      const fuzzy = lows.find((o) => lc.includes(o));
      if (fuzzy) return col;
    }
    return '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setCsvFile(file);
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
        setCsvData(data);
        const csvCols = Object.keys(data[0] || {});
        setColumns(csvCols);

        setQuestionColumn(findBestMatch(csvCols, expectedColumns.questionColumn));
        setModelColumn(findBestMatch(csvCols, expectedColumns.modelColumn));
        setBaselineAnswerSourcesColumn(
          findBestMatch(csvCols, expectedColumns.baselineAnswerSourcesColumn),
        );
        setBaselineChunkColumn(findBestMatch(csvCols, expectedColumns.baselineChunkColumn));
        setBaselineAnswerColumn(findBestMatch(csvCols, expectedColumns.baselineAnswerColumn));
      },
      error: (err) => console.error('CSV parse error:', err),
    });
  };

  const handleCriteriaToggle = (e) => {
    const { name, checked } = e.target;
    setEvaluationCriteria((prev) =>
      checked ? [...prev, name] : prev.filter((c) => c !== name),
    );
  };

  const handleUpload = () => {
    if (!questionColumn || !modelColumn) {
      alert('Please select both Question and Model columns.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) {
      alert('Please enter a valid email address.');
      return;
    }

    const preparedData = csvData.map((row) => ({
      question: row[questionColumn],
      tenant_model: row[modelColumn],
      answer_sources: row[baselineAnswerSourcesColumn],
      chunks: row[baselineChunkColumn],
      answer: row[baselineAnswerColumn],
    }));

    handleCSVUpload({
      preparedData,
      userEmail,
      evaluationCriteria,
      mode,
    });
  };

  const ColumnSelect = ({ label, value, onChange, optional }) => (
    <FormControl fullWidth sx={{ mb: 3 }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        {optional && (
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
        )}
        {columns.map((col) => (
          <MenuItem key={col} value={col}>
            {col}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      {/* Top title + help toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Upload a CSV File</Typography>
        <IconButton onClick={() => setShowHelp(!showHelp)}>
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Help section */}
      <Collapse in={showHelp}>
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: '1px dashed #999',
            borderRadius: 2,
            backgroundColor: '#f9f9f9',
          }}
        >
          <Typography variant="body2" gutterBottom>
            Upload a CSV file where each row represents a user query for a specific model.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Required Columns:</strong>
            <ul>
              <li><strong>Question Column</strong>: Contains the user query/prompt.</li>
              <li><strong>Model Column</strong>: The model ID or name to use for the row.</li>
            </ul>
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Optional Columns: </strong>
            These are not used, but you can include them for comparison.
            <ul>
              <li><strong>Answer Sources</strong>: Chunks used to generate answer.</li>
              <li><strong>Chunks</strong>: All chunks retrieved by knowledge</li>
              <li><strong>Answer</strong>: Previously generated answer.</li>
            </ul>
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Email</strong>: You’ll receive notification via slack with updates.
          </Typography>
        </Box>
      </Collapse>

      {/* Choose file */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button variant="outlined" component="label">
          Choose File
          <input type="file" accept=".csv" hidden onChange={handleFileChange} />
        </Button>
        <Typography variant="body2">
          {csvFile ? csvFile.name : 'No file selected'}
        </Typography>
      </Box>

      {/* Column Selectors */}
      {columns.length > 0 && (
        <>
          <ColumnSelect
            label="Question Column"
            value={questionColumn}
            onChange={setQuestionColumn}
          />
          <ColumnSelect
            label="Model Column"
            value={modelColumn}
            onChange={setModelColumn}
          />
          <ColumnSelect
            label="Answer Sources (optional)"
            value={baselineAnswerSourcesColumn}
            onChange={setBaselineAnswerSourcesColumn}
            optional
          />
          <ColumnSelect
            label="Chunks (optional)"
            value={baselineChunkColumn}
            onChange={setBaselineChunkColumn}
            optional
          />
          <ColumnSelect
            label="Answer (optional)"
            value={baselineAnswerColumn}
            onChange={setBaselineAnswerColumn}
            optional
          />
        </>
      )}

      {/* Evaluation Criteria */}
      {/* <Typography variant="subtitle1" sx={{ mt: 2 }}>
        Evaluation Criteria
      </Typography> */}
      {/* <FormGroup sx={{ mb: 3 }}>
        {evaluationCriteriaOptions.map((opt) => (
          <FormControlLabel
            key={opt}
            control={
              <Checkbox
                checked={evaluationCriteria.includes(opt)}
                onChange={handleCriteriaToggle}
                name={opt}
              />
            }
            label={opt}
          />
        ))}
      </FormGroup> */}

      {/* Email field */}
      <TextField
        label="Send notification to:"
        type="email"
        fullWidth
        value={userEmail}
        onChange={(e) => setUserEmail(e.target.value.trim())}
        sx={{ mb: 3 }}
      />

      {/* Upload Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleUpload}
        disabled={isSubmitting || !csvFile}
        fullWidth
      >
        Upload CSV
      </Button>
    </Box>
  );
};

export default UploadCSV;